import { __decorate } from 'tslib';
// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { sharedStyles } from '../../styles';
import { ShuiLitElement } from '../../utils/event-decorator.js';
import navbarStyles from './sh-nav-bar.lit.scss.js';
/**
 * @slot - Primary slot for navigation content. Takes sh-nav-group with sh-nav-item or sh-tabs with sh-tab-item as children.
 * @slot functions - Holds page functions such as Save, Export, Delete, etc. Takes icons as children.
 */
let SHNavBar = class SHNavBar extends ShuiLitElement {
  constructor() {
    super(...arguments);
    /** Indicates whether the functions slot is empty. Used to conditionally hide the functions wrapper when no function icons are provided. */
    this.emptyFunction = true;
    /** Indicates whether the nav bar has tabs. */
    this.hasTabs = false;
  }
  static get styles() {
    return [sharedStyles, navbarStyles];
  }
  render() {
    const wrapperClass = !this.hasTabs ? 'non-tabs-mode' : '';
    return html`
      <div class="nav-bar-wrapper ${wrapperClass}">
        <div class="tabs-wrapper">
          <slot id="tabs" @slotchange=${() => this.tabsSlotUpdated()}></slot>
        </div>
        <div class="functions-wrapper" ?empty-function="${this.emptyFunction}">
          <slot
            name="functions"
            @slotchange="${this.handleSlotNodes.bind(this)}"
            id="functionsSlot"
          ></slot>
        </div>
      </div>
    `;
  }
  tabsSlotUpdated() {
    const tabs = this.querySelectorAll('sh-tabs');
    this.hasTabs = tabs.length > 0;
    // trigger nav prop of tabs
    if (this.slot === 'nav') {
      for (const tab of tabs) {
        tab.setAttribute('nav-context', '');
      }
    }
    // trigger footer prop of tabs
    if (this.slot === 'footer') {
      for (const tab of tabs) {
        tab.setAttribute('footer-context', '');
      }
    }
    this.handleCondensed();
  }
  update(changedProperties) {
    super.update(changedProperties);
    if (changedProperties.has('condensed')) {
      this.handleCondensed();
    }
  }
  handleCondensed() {
    const tabItems = this.querySelectorAll('sh-tab-item');
    const tabs = this.querySelectorAll('sh-tabs');
    tabs.forEach((el) => {
      el.condensed = this.condensed;
    });
    tabItems.forEach((el) => {
      el.condensed = this.condensed;
    });
  }
  handleSlotNodes() {
    var _a;
    const functionSlot =
      (_a = this.shadowRoot) === null || _a === void 0
        ? void 0
        : _a.querySelector('#functionsSlot');
    const functionNodesLength = functionSlot.assignedNodes({
      flatten: true,
    }).length;
    this.emptyFunction = functionNodesLength === 0;
  }
};
__decorate([property({ type: Boolean, reflect: true })], SHNavBar.prototype, 'condensed', void 0);
__decorate([state()], SHNavBar.prototype, 'emptyFunction', void 0);
__decorate([state()], SHNavBar.prototype, 'hasTabs', void 0);
SHNavBar = __decorate([customElement('sh-nav-bar')], SHNavBar);
export { SHNavBar };
